import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Switch } from '@mui/material';
import { FiInfo } from 'react-icons/fi';
import { showToast } from "../../Components/utils/ShowToast";
import Loader from '../../Components/utils/Loader';
import { tommorrow, FEE_CATEGORIES, FEE_CATEGORY_LABELS } from "../../data/constants";

const COMMISSION_GROUPS = [
    { key: 'freeSeller', label: 'Free seller' },
    { key: 'paidSeller', label: 'Paid seller' },
    { key: 'buyer', label: 'Buyer' }
];

const emptyCommissions = () => COMMISSION_GROUPS.reduce((groups, { key }) => {
    groups[key] = FEE_CATEGORIES.reduce((row, category) => ({ ...row, [category]: 0 }), {});
    return groups;
}, {});

const PAYOUT_FEE_METHODS = [
    {
        key: 'stripe',
        label: 'Stripe',
        note: 'Stripe bills the platform for Express account payout, cross-border and currency conversion charges. Set a fee here to recover them from the seller.'
    },
    {
        key: 'wise',
        label: 'Wise',
        note: 'Wise deducts its transfer fee from the sending (platform) account and the rate varies by currency corridor. Enable it only if you want to pass an estimated fee on to sellers.'
    },
    {
        key: 'paypal',
        label: 'PayPal',
        note: 'PayPal charges its payout fee to the sending (platform) account. The seller receives the net amount shown in the payout CSV.'
    }
];

function AdminFee() {
    const [isLoading, setIsLoading] = useState(true);
    const [commissions, setCommissions] = useState(emptyCommissions);
    const [membership, setMembership] = useState({
        threeMonths: 0,
        sixMonths: 0,
        twelveMonths: 0,
        offerDiscount: false,
        discountType: 'onAllPlans',
        discounts: {
            allPlans: { discount: 0, expiryDate: '' },
            threeMonths: { discount: 0, expiryDate: '' },
            sixMonths: { discount: 0, expiryDate: '' },
            twelveMonths: { discount: 0, expiryDate: '' }
        }
    });
    const [payoutFees, setPayoutFees] = useState({
        stripe: { enabled: true, percent: 0, fixed: 0 },
        wise: { enabled: false, percent: 0, fixed: 0 },
        paypal: { enabled: true, percent: 0, fixed: 0 }
    });
    const [boosts, setBoosts] = useState({ enabled: true, packages: [] });
    const [editMode, setEditMode] = useState({
        fees: false,
        payoutFees: false,
        membership: false,
        boosts: false
    });

    const [originalCommissions, setOriginalCommissions] = useState(emptyCommissions);
    const [originalPayoutFees, setOriginalPayoutFees] = useState({ ...payoutFees });
    const [originalMembership, setOriginalMembership] = useState({ ...membership });
    const [originalBoosts, setOriginalBoosts] = useState({ enabled: true, packages: [] });

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/feesAndMembership`)
            .then(response => {
                if (response.data.success) {
                    if (response.data.commissions) {
                        // Already resolved server-side: any category the admin
                        // has never set comes back carrying the rate actually
                        // being charged today.
                        setCommissions(response.data.commissions);
                        setOriginalCommissions(response.data.commissions);
                    }
                    if (response.data.payoutFees) {
                        setPayoutFees(response.data.payoutFees);
                        setOriginalPayoutFees(response.data.payoutFees);
                    }
                    if (response.data.membership) {
                        const membershipData = response.data.membership;

                        const convertDate = (date) => date ? new Date(date).toISOString().split('T')[0] : '';

                        if (membershipData.discounts) {
                            membershipData.discounts.allPlans.expiryDate = convertDate(membershipData.discounts.allPlans.expiryDate);
                            membershipData.discounts.threeMonths.expiryDate = convertDate(membershipData.discounts.threeMonths.expiryDate);
                            membershipData.discounts.sixMonths.expiryDate = convertDate(membershipData.discounts.sixMonths.expiryDate);
                            membershipData.discounts.twelveMonths.expiryDate = convertDate(membershipData.discounts.twelveMonths.expiryDate);
                        }

                        setMembership(membershipData);
                        setOriginalMembership(membershipData);
                    }
                }
            }).finally(() => setIsLoading(false));

        // Boost packages sit behind the admin token, so they load separately
        // from the public fees/membership endpoint above.
        const adminToken = localStorage.getItem('adminToken');
        axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/boosts/admin/settings`, {
            headers: { Authorization: `Bearer ${adminToken}` }
        })
            .then(response => {
                if (response.data.success) {
                    setBoosts(response.data.boosts);
                    setOriginalBoosts(response.data.boosts);
                }
            })
            .catch(() => { /* Section renders with defaults if this fails. */ });
    }, []);

    const handleBoostFieldChange = (index, field, value) => {
        setBoosts(prev => ({
            ...prev,
            packages: prev.packages.map((p, i) => (i === index ? { ...p, [field]: value } : p))
        }));
    };

    const handleAddBoostPackage = () => {
        setBoosts(prev => ({ ...prev, packages: [...prev.packages, { days: '', price: '', isActive: true }] }));
    };

    const handleRemoveBoostPackage = (index) => {
        setBoosts(prev => ({ ...prev, packages: prev.packages.filter((_, i) => i !== index) }));
    };

    const handleBoostSubmit = () => {
        if (!editMode.boosts) {
            setEditMode({ ...editMode, boosts: true });
            return;
        }

        // The server sanitises too, but catching it here spares the admin a
        // silently-dropped row.
        const invalid = boosts.packages.some(p => !Number(p.days) || Number(p.days) < 1 || Number(p.price) < 0);
        if (invalid) {
            showToast('Each package needs a duration of at least 1 day and a price of 0 or more.', 'error');
            return;
        }
        const days = boosts.packages.map(p => Number(p.days));
        if (new Set(days).size !== days.length) {
            showToast('Two packages cannot have the same duration.', 'error');
            return;
        }

        setIsLoading(true);
        const token = localStorage.getItem('adminToken');
        axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/boosts/admin/settings`, { boosts }, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then((response) => {
                showToast('Boost Packages Updated!', 'success');
                setEditMode({ ...editMode, boosts: false });
                setBoosts(response.data.boosts);
                setOriginalBoosts(response.data.boosts);
            })
            .catch((e) => {
                showToast(e?.response?.data?.message || 'Failed to Update Boost Packages!', 'error');
            }).finally(() => setIsLoading(false));
    };

    const handleBoostCancel = () => {
        setBoosts({ ...originalBoosts });
        setEditMode({ ...editMode, boosts: false });
    };

    const handleCommissionChange = (group, category, value) => {
        if (value !== '' && (Number.isNaN(Number(value)) || Number(value) < 0 || Number(value) > 100)) return;
        setCommissions(prev => ({
            ...prev,
            [group]: { ...prev[group], [category]: value }
        }));
    };

    const handlePayoutFeeChange = (method, field, value) => {
        setPayoutFees(prev => ({
            ...prev,
            [method]: { ...prev[method], [field]: value }
        }));
    };

    const handlePayoutFeeSubmit = () => {
        if (!editMode.payoutFees) {
            setEditMode({ ...editMode, payoutFees: true });
            return;
        }

        setIsLoading(true);
        const token = localStorage.getItem('adminToken');
        axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/update/payout-fees`, { payoutFees }, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then((response) => {
                showToast('Payout Fees Updated!', 'success');
                setEditMode({ ...editMode, payoutFees: false });
                setPayoutFees(response.data.payoutFees);
                setOriginalPayoutFees(response.data.payoutFees);
            })
            .catch((e) => {
                showToast(e?.response?.data?.message || 'Failed to Update Payout Fees!', 'error');
            }).finally(() => setIsLoading(false));
    };

    const handlePayoutFeeCancel = () => {
        setPayoutFees({ ...originalPayoutFees });
        setEditMode({ ...editMode, payoutFees: false });
    };

    const handleMembershipChange = (e) => {
        const { name, value, type, checked } = e.target;
        setMembership({
            ...membership,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleDiscountChange = (e, plan) => {
        const { name, value } = e.target;
        setMembership({
            ...membership,
            discounts: {
                ...membership.discounts,
                [plan]: {
                    ...membership.discounts[plan],
                    [name]: value
                }
            }
        });
    };

    const handleFeeSubmit = () => {
        if (!editMode.fees) {
            setEditMode({ ...editMode, fees: true });
            return;
        }

        const blank = COMMISSION_GROUPS.some(({ key }) =>
            FEE_CATEGORIES.some((category) => commissions[key]?.[category] === '')
        );
        if (blank) {
            showToast('Every fee needs a value. Enter 0 to charge nothing.', 'error');
            return;
        }

        setIsLoading(true);
        const token = localStorage.getItem('adminToken');
        axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/update/fees`, { commissions }, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then((response) => {
                showToast(`Fees Updated!`, 'success');
                setEditMode({ ...editMode, fees: false });
                const saved = response.data.commissions || commissions;
                setCommissions(saved);
                setOriginalCommissions(saved);
            })
            .catch((e) => {
                showToast(e?.response?.data?.error || 'Failed to Update Fees!', 'error');
            }).finally(() => setIsLoading(false));
    };

    const handleMembershipSubmit = () => {
        if (!editMode.membership) {
            setEditMode({ ...editMode, membership: true });
            return;
        }

        setIsLoading(true);
        const token = localStorage.getItem('adminToken');
        axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/update/membership`, { membership }, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(() => {
                showToast(`Membership Updated!`, 'success');
                setEditMode({ ...editMode, membership: false });
            }).finally(() => setIsLoading(false));
    };

    const renderDiscountFields = () => {
        if (!membership.offerDiscount) return null;

        if (membership.discountType === 'onAllPlans') {
            return (
                <div className="inputDiv">
                    <div className="inputInnerDiv">
                        <label>Discount on all Plans (%)</label>
                        <input
                            type="number"
                            name="discount"
                            value={membership.discounts.allPlans.discount}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value.length <= 3 && value <= 100)
                                    handleDiscountChange(e, 'allPlans')
                            }}
                            className='inputField'
                            placeholder='Enter Discount (%)'
                            disabled={!editMode.membership}
                        />
                    </div>
                    <div className="inputInnerDiv">
                        <label>Expiry Date</label>
                        <input
                            type="date"
                            name="expiryDate"
                            value={membership.discounts.allPlans.expiryDate}
                            onChange={(e) => handleDiscountChange(e, 'allPlans')}
                            className='inputField'
                            disabled={!editMode.membership}
                            min={tommorrow}
                        />
                    </div>
                </div>
            );
        } else {
            return ['threeMonths', 'sixMonths', 'twelveMonths'].map((plan, index) => (
                <div className="inputDiv" key={index}>
                    <div className="inputInnerDiv">
                        <label>Discount on {(index + 1) * 3} Months Plan (%)</label>
                        <input
                            type="number"
                            name="discount"
                            value={membership.discounts[plan].discount}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value.length <= 3 && value <= 100)
                                    handleDiscountChange(e, plan)
                            }}
                            className='inputField'
                            placeholder='Enter Discount (%)'
                            disabled={!editMode.membership}
                        />
                    </div>
                    <div className="inputInnerDiv">
                        <label>Expiry Date</label>
                        <input
                            type="date"
                            name="expiryDate"
                            value={membership.discounts[plan].expiryDate}
                            onChange={(e) => handleDiscountChange(e, plan)}
                            className='inputField'
                            disabled={!editMode.membership}
                            min={tommorrow}
                        />
                    </div>
                </div>
            ));
        }
    };

    const handleCancel = () => {
        setCommissions(originalCommissions);
        setEditMode({ ...editMode, fees: false });
    };

    const handleMembershipCancel = () => {
        setMembership(originalMembership);
        setEditMode({ ...editMode, membership: false });
    };

    return (
        <div className='adminFeeDiv'>

            {isLoading && <Loader type="checkmate" />}
            
            <h1 className="primaryHeading"><span>Fee</span> Management</h1>
            <div className="adminFeeContent">
                <div className="left">
                    <div className="box">
                        <div className="upper">
                            <h2 className="secondaryHeading"><span>Transaction</span> Fees</h2>
                        </div>
                        <div className="lower form">
                            <p className="payoutFeeIntro">
                                The commission Chocowi takes on each transaction, charged to the
                                seller on their earnings and to the buyer at checkout. Every rate
                                is a percentage; enter 0 to charge nothing.
                            </p>

                            <div className="commissionTable">
                                <div className="commissionHeaderRow">
                                    <span className="commissionRowLabel" />
                                    {COMMISSION_GROUPS.map(({ key, label }) => (
                                        <span className="commissionHeaderCell" key={key}>{label}</span>
                                    ))}
                                </div>

                                {FEE_CATEGORIES.map((category) => (
                                    <div className="commissionRow" key={category}>
                                        <span className="commissionRowLabel">{FEE_CATEGORY_LABELS[category]}</span>
                                        {COMMISSION_GROUPS.map(({ key, label }) => (
                                            <input
                                                key={key}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                value={commissions[key]?.[category] ?? 0}
                                                onChange={(e) => handleCommissionChange(key, category, e.target.value)}
                                                className='inputField'
                                                placeholder='0'
                                                aria-label={`${label} — ${FEE_CATEGORY_LABELS[category]} (%)`}
                                                disabled={!editMode.fees}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>

                            <p className="payoutFeeNote">
                                <FiInfo className="payoutFeeNoteIcon" />
                                A listing counts as a service when it is created under a service
                                category, and as B2B when it is sold through the wholesale
                                marketplace. Shipping distance does not affect the rate. Rates are
                                locked in when an order is placed — changes here never re-price an
                                existing order.
                            </p>

                            <div className="buttonsDiv">
                                <button className='secondaryBtn' disabled={!editMode.fees} onClick={handleCancel}>Cancel</button>
                                <button className="secondaryBtn" onClick={handleFeeSubmit}>
                                    {editMode.fees ? 'Update' : 'Edit'}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="box">
                        <div className="upper">
                            <h2 className="secondaryHeading"><span>Payout</span> Fees</h2>
                        </div>
                        <div className="lower form">
                            <p className="payoutFeeIntro">
                                Charged to the seller when a withdrawal is paid out, to recover the
                                fees the payment provider charges the platform. Leave both fields at
                                0 to charge nothing.
                            </p>
                            {PAYOUT_FEE_METHODS.map(({ key, label, note }) => (
                                <div className="payoutFeeMethod" key={key}>
                                    <div className="payoutFeeMethodHeader">
                                        <label>{label}</label>
                                        <Switch
                                            checked={payoutFees[key].enabled}
                                            onChange={(e) => handlePayoutFeeChange(key, 'enabled', e.target.checked)}
                                            disabled={!editMode.payoutFees}
                                            color="primary"
                                            size="small"
                                        />
                                    </div>
                                    <div className="inputDiv">
                                        <div className="inputInnerDiv">
                                            <label>Percentage (%)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={payoutFees[key].percent}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || (Number(value) >= 0 && Number(value) <= 100))
                                                        handlePayoutFeeChange(key, 'percent', value);
                                                }}
                                                className='inputField'
                                                placeholder='Enter Fee (%)'
                                                disabled={!editMode.payoutFees || !payoutFees[key].enabled}
                                            />
                                        </div>
                                        <div className="inputInnerDiv">
                                            <label>Fixed Amount ($)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={payoutFees[key].fixed}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || Number(value) >= 0)
                                                        handlePayoutFeeChange(key, 'fixed', value);
                                                }}
                                                className='inputField'
                                                placeholder='Enter Fee ($)'
                                                disabled={!editMode.payoutFees || !payoutFees[key].enabled}
                                            />
                                        </div>
                                    </div>
                                    <p className="payoutFeeNote"><FiInfo className="payoutFeeNoteIcon" /> {note}</p>
                                </div>
                            ))}
                            <div className="buttonsDiv">
                                <button className='secondaryBtn' disabled={!editMode.payoutFees} onClick={handlePayoutFeeCancel}>Cancel</button>
                                <button className="secondaryBtn" onClick={handlePayoutFeeSubmit}>
                                    {editMode.payoutFees ? 'Update' : 'Edit'}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="box">
                        <div className="upper">
                            <h2 className="secondaryHeading"><span>Listing</span> Boosts</h2>
                        </div>
                        <div className="lower form">
                            <p className="payoutFeeIntro">
                                Sellers can pay to promote a listing to the top of search and
                                category results, above both Premium and Basic sellers. Turning
                                boosts off hides the option from sellers; promotions already
                                running are unaffected and expire normally.
                            </p>

                            <div className="payoutFeeMethodHeader">
                                <label>Boosts available to sellers</label>
                                <Switch
                                    checked={boosts.enabled}
                                    onChange={(e) => setBoosts(prev => ({ ...prev, enabled: e.target.checked }))}
                                    disabled={!editMode.boosts}
                                    color="primary"
                                    size="small"
                                />
                            </div>

                            {boosts.packages.map((pkg, index) => (
                                <div className="boostPackageRow" key={index}>
                                    <div className="inputDiv">
                                        <div className="inputInnerDiv">
                                            <label>Duration (days)</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={pkg.days}
                                                onChange={(e) => handleBoostFieldChange(index, 'days', e.target.value)}
                                                className='inputField'
                                                placeholder='e.g. 7'
                                                disabled={!editMode.boosts}
                                            />
                                        </div>
                                        <div className="inputInnerDiv">
                                            <label>Price ($)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={pkg.price}
                                                onChange={(e) => handleBoostFieldChange(index, 'price', e.target.value)}
                                                className='inputField'
                                                placeholder='e.g. 6.00'
                                                disabled={!editMode.boosts}
                                            />
                                        </div>
                                    </div>
                                    <div className="boostPackageRowActions">
                                        <label className="boostPackageActiveToggle">
                                            <Switch
                                                checked={pkg.isActive !== false}
                                                onChange={(e) => handleBoostFieldChange(index, 'isActive', e.target.checked)}
                                                disabled={!editMode.boosts}
                                                color="primary"
                                                size="small"
                                            />
                                            <span title="Controls whether sellers can buy this package. Turning it off hides the duration from the boost dialog; boosts already running on it continue and expire normally.">
                                                {pkg.isActive !== false ? 'Offered to sellers' : 'Not offered'}
                                            </span>
                                        </label>
                                        <button
                                            type="button"
                                            className="boostPackageRemove"
                                            onClick={() => handleRemoveBoostPackage(index)}
                                            disabled={!editMode.boosts}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {editMode.boosts && (
                                <button type="button" className="secondaryBtn boostPackageAdd" onClick={handleAddBoostPackage}>
                                    + Add package
                                </button>
                            )}

                            <p className="payoutFeeNote">
                                <FiInfo className="payoutFeeNoteIcon" />
                                Boost revenue appears under "From Boosts" on the Revenue page. Tax is
                                added at checkout based on the seller's own address. Switch a package
                                to "Not offered" to retire a duration without deleting it — sellers
                                stop seeing it, and boosts already running on it finish normally.
                            </p>

                            <div className="buttonsDiv">
                                <button className='secondaryBtn' disabled={!editMode.boosts} onClick={handleBoostCancel}>Cancel</button>
                                <button className="secondaryBtn" onClick={handleBoostSubmit}>
                                    {editMode.boosts ? 'Update' : 'Edit'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="right box">
                    <div className="upper">
                        <h2 className="secondaryHeading"><span>Paid</span> Membership</h2>
                    </div>
                    <div className="lower form">
                        <div className="inputDiv">
                            <label>3 Months ($)</label>
                            <input
                                type="number"
                                name="threeMonths"
                                value={membership.threeMonths}
                                onChange={handleMembershipChange}
                                className='inputField'
                                placeholder='Enter Price ($)'
                                disabled={!editMode.membership}
                            />
                        </div>
                        <div className="inputDiv">
                            <label>6 Months ($)</label>
                            <input
                                type="number"
                                name="sixMonths"
                                value={membership.sixMonths}
                                onChange={handleMembershipChange}
                                className='inputField'
                                placeholder='Enter Price ($)'
                                disabled={!editMode.membership}
                            />
                        </div>
                        <div className="inputDiv">
                            <label>12 Months ($)</label>
                            <input
                                type="number"
                                name="twelveMonths"
                                value={membership.twelveMonths}
                                onChange={handleMembershipChange}
                                className='inputField'
                                placeholder='Enter Price ($)'
                                disabled={!editMode.membership}
                            />
                        </div>
                        <div className="checkboxDiv">
                            <input
                                type="checkbox"
                                className="checkbox"
                                id="offerDiscount"
                                name="offerDiscount"
                                checked={membership.offerDiscount}
                                onChange={handleMembershipChange}
                                disabled={!editMode.membership}
                            />
                            <label htmlFor="offerDiscount">Offer Discount</label>
                        </div>

                        {membership.offerDiscount && (
                            <>
                                <div className="discountType inputDiv">
                                    <div className="radioBtn">
                                        <input
                                            type="radio"
                                            name="discountType"
                                            id="onAllPlans"
                                            value="onAllPlans"
                                            checked={membership.discountType === 'onAllPlans'}
                                            onChange={handleMembershipChange}
                                            disabled={!editMode.membership}
                                        />
                                        <label htmlFor="onAllPlans">On All Plans</label>
                                    </div>
                                    <div className="radioBtn">
                                        <input
                                            type="radio"
                                            name="discountType"
                                            id="individualDiscount"
                                            value="individualDiscount"
                                            checked={membership.discountType === 'individualDiscount'}
                                            onChange={handleMembershipChange}
                                            disabled={!editMode.membership}
                                        />
                                        <label htmlFor="individualDiscount">Individual Discount</label>
                                    </div>
                                </div>
                                <div className="horizontalLine"></div>
                                {renderDiscountFields()}
                            </>
                        )}

                        <div className="buttonsDiv">
                            <button className='secondaryBtn' disabled={!editMode.membership} onClick={handleMembershipCancel}>Cancel</button>
                            <button className="secondaryBtn" onClick={handleMembershipSubmit}>
                                {editMode.membership ? 'Update' : 'Edit'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminFee;